import { MathManager } from '@payments-checkout/shared/helpers/managers/mathManager';
import { acceptedPaymentMethod } from '../../types/customer.types';
import {
  BrandInstallments,
  PaymentInstallments,
  SinglePaymentLinkResponse,
} from '../../types/paymentLink.types';
import { SummaryProps } from '../../types/sumaryCard.types';

type SummaryBuilderProps = {
  selectedPaymentMethod: acceptedPaymentMethod;
  data?: SinglePaymentLinkResponse;
  installments?: number | null;
};

type SummaryBuilderWithRate = {
  selectedPaymentMethod: acceptedPaymentMethod;
  data?: SinglePaymentLinkResponse;
  installments?: number | null;
  brand?: string;
  useBrand?: boolean;
};

const getSettingsByPaymentMethod = (
  data: SinglePaymentLinkResponse | undefined,
  selectedPaymentMethod: acceptedPaymentMethod
) => {
  if (!selectedPaymentMethod || selectedPaymentMethod === 'debit_card') {
    return undefined;
  }

  switch (selectedPaymentMethod) {
    case 'credit_card':
      return data?.payment_settings.credit_card_settings;
    case 'pix':
      return data?.payment_settings.pix_settings;
    case 'boleto':
      return data?.payment_settings.boleto_settings;
    default:
      return undefined;
  }
};

const checkoutDetailItemsBuilder = (
  pureItems?: SinglePaymentLinkResponse['cart_settings']['items']
) => {
  if (!pureItems) return [];
  return pureItems.map((item) => {
    const unitDescription =
      item.default_quantity === 1
        ? 'info.singleUnit.label'
        : 'info.multipleUnit.label';
    return {
      name: item.name,
      description: item.description,
      unit: item.default_quantity,
      unitDescription,
      value:
        item.default_quantity > 1
          ? `${MathManager.formatCurrency({ value: item.amount / 100 })} /un.`
          : MathManager.formatCurrency({ value: item.amount / 100 }),
    };
  });
};

const buildCommonSummaryValues = (props: {
  paymentDiscountInValue?: number;
  returnValues: Record<string, string>;
  paymentDiscountInPercentage?: number;
  discountInPercentage: number;
  total: number;
  subtotal: number;
  totalShippingCost?: number;
}) => {
  const {
    paymentDiscountInValue,
    returnValues,
    paymentDiscountInPercentage,
    discountInPercentage,
    total,
    subtotal,
    totalShippingCost,
  } = props;

  if (subtotal !== 0) {
    Object.assign(returnValues, {
      subtotal: MathManager.formatCurrency({ value: subtotal / 100 }),
    });
  }

  if (totalShippingCost) {
    Object.assign(returnValues, {
      shipping: MathManager.formatCurrency({ value: totalShippingCost / 100 }),
    });
  }

  if (paymentDiscountInValue && paymentDiscountInValue > 0) {
    Object.assign(returnValues, {
      discount: MathManager.formatCurrency({
        value: -(paymentDiscountInValue / 100),
      }),
    });
  }

  if (paymentDiscountInPercentage && paymentDiscountInPercentage > 0) {
    Object.assign(returnValues, {
      discount: `${MathManager.formatCurrency({
        value: -(discountInPercentage / 100),
      })} (${paymentDiscountInPercentage}%)`,
    });
  }

  const formattedTotal = MathManager.formatCurrency({ value: total / 100 });

  const returnItems: SummaryProps['items'] = Object.entries(returnValues).map(
    ([key, value]) => ({
      title: key,
      description: {
        color: key === 'discount' ? 'positive' : 'medium',
        weight: 'semibold',
        content: value,
      },
    })
  );
  return {
    items: returnItems,
    total: {
      amount: total,
      description: 'total',
      formattedAmount: total === 0 ? '--' : formattedTotal,
    },
  };
};

const summaryBuilderWithRate = ({
  data,
  selectedPaymentMethod,
  installments,
  brand,
}: SummaryBuilderWithRate) => {
  const returnValues: Record<string, string> = {};
  const items = data?.cart_settings?.items;
  const totalShippingCost = data?.cart_settings.shipping_total_cost;
  const subtotal = (items ?? []).reduce(
    (acc, curr) => acc + curr.default_quantity * curr.amount,
    0
  );
  const brandsInstallments =
    data?.payment_settings.credit_card_settings?.brand_installments;

  const totalValue =
    brandsInstallments?.find((installment) => {
      return installment.brand.toLowerCase() === brand?.toLocaleLowerCase();
    }) ??
    brandsInstallments?.find((installment) => {
      return installment.brand.includes('DEFAULT');
    });

  const totalBySelectedInstallments =
    totalValue?.installments[(installments ?? 1) - 1].total ?? 0;

  const selectedPaymentSettings = getSettingsByPaymentMethod(
    data,
    selectedPaymentMethod
  );
  const paymentDiscountInValue = selectedPaymentSettings?.discount ?? 0;
  const paymentDiscountInPercentage =
    selectedPaymentSettings?.discount_percentage ?? 0;

  const discountInPercentage = MathManager.percentageToValue(
    paymentDiscountInPercentage ?? 0,
    totalBySelectedInstallments
  );

  const total = totalBySelectedInstallments -
    (paymentDiscountInValue ?? 0) -
    MathManager.percentageToValue(paymentDiscountInPercentage ?? 0, subtotal);

  return buildCommonSummaryValues({
    discountInPercentage,
    returnValues,
    subtotal,
    total,
    paymentDiscountInPercentage,
    paymentDiscountInValue,
    totalShippingCost,
  });
};

const summaryBuilder = ({
  data,
  selectedPaymentMethod,
  installments
}: SummaryBuilderProps) => {
  const returnValues: Record<string, string> = {};
  const items = data?.cart_settings?.items;
  const totalShippingCost = data?.cart_settings.shipping_total_cost;
  const subtotal = (items ?? []).reduce(
    (acc, curr) => acc + curr.default_quantity * curr.amount,
    0
  );
  const selectedPaymentSettings = getSettingsByPaymentMethod(
    data,
    selectedPaymentMethod
  );
  const paymentDiscountInValue = selectedPaymentSettings?.discount ?? 0;
  const paymentDiscountInPercentage =
    selectedPaymentSettings?.discount_percentage ?? 0;

  const discountInPercentage = MathManager.percentageToValue(
    paymentDiscountInPercentage ?? 0,
    subtotal
  );

  const preTotal =
    subtotal +
    (totalShippingCost ?? 0) -
    (paymentDiscountInValue ?? 0) -
    MathManager.percentageToValue(paymentDiscountInPercentage ?? 0, subtotal);

  let total = preTotal
  if (selectedPaymentMethod === 'credit_card' && installments) {
    const creditCardInstallments = data?.payment_settings.credit_card_settings?.installments
    const selectedInstallment = creditCardInstallments?.find((item) => item.number === installments)
    total = selectedInstallment?.total ?? preTotal
  }

    return buildCommonSummaryValues({
      discountInPercentage,
      returnValues,
      subtotal,
      total,
      paymentDiscountInPercentage,
      paymentDiscountInValue,
      totalShippingCost,
    });
};


const withoutBrandInstallmentRateInstallment = (
  installments?: PaymentInstallments,
  organization?: string,
  totalCost?:number
) => {
  if (!installments || installments?.length === 0) return [];

  const generateInstallments = Array.from(
    { length: installments.length },
    (_, index) => {
      const { total } = installments[index];
      const value = total / installments[index].number;
      return {
        label: `${installments[index].number}x de ${MathManager.formatCurrency({
          value: (value / 100).toFixed(2),
        })}`,
        value: (installments[index].number).toString(),
        total: totalCost || 0,
        totalWithSurcharges: total,
        useRate: false,
      };
    }
  );

  return generateInstallments;
};

const installmentWithBrandRate = (
  brand?: string,
  installments?: BrandInstallments
) => {
  if (!installments || installments?.length === 0 || !brand) return [];

  const brandInstallment =
    installments.find(
      (installment) =>
        installment.brand.toLowerCase() === brand.toLocaleLowerCase()
    ) || installments.find((installment) => installment.brand === 'DEFAULT');

  const generateInstallments = Array.from(
    { length: brandInstallment?.installments.length ?? 0 },
    (_, index) => {
      const value =
        (brandInstallment?.installments[index]?.total ?? 0) / 100 / (index + 1);

      return {
        label: `${index + 1}x de ${MathManager.formatCurrency({
          value: value.toFixed(2),
        })}`,
        value: (index + 1).toString(),
        total: brandInstallment?.installments[0].total ?? 0,
        totalWithSurcharges: brandInstallment?.installments[index]?.total,
        useRate:
          (brandInstallment?.installments[index]?.interest_rate ?? 0) > 0,
      };
    }
  );

  return generateInstallments;
};

const installmentsBuilder = ({
  value,
  brand,
  organization,
  totalCost
}: {
  value: SinglePaymentLinkResponse['payment_settings']['credit_card_settings'];
  brand?: string;
  organization?: string;
  totalCost?: number;
}) => {
  switch (Boolean(value?.use_brand_interest_rate)) {
    case true:
      return installmentWithBrandRate(brand, value?.brand_installments);
    case false:
      return withoutBrandInstallmentRateInstallment(
        value?.installments,
        organization,
        totalCost
      );
    default:
      return [];
  }
};

const summaryBuilderWithConditions = ({
  selectedPaymentMethod,
  brand = 'DEFAULT',
  data,
  installments,
  useBrand,
}: SummaryBuilderWithRate) => {
  if (useBrand)
    return summaryBuilderWithRate({
      data,
      selectedPaymentMethod,
      installments,
      brand,
    });

  return summaryBuilder({ data, selectedPaymentMethod, installments });
};

const hasAnyDigitalWallet = (data: SinglePaymentLinkResponse) => {
  return (
    data.payment_settings.apple_pay_enabled ||
    data.payment_settings.google_pay_enabled ||
    data.payment_settings.click_to_pay_enabled
  );
};

const SharedPageBuilder = {
  itemsBuilder: checkoutDetailItemsBuilder,
  summaryBuilder,
  summaryBuilderWithConditions,
  installmentsBuilder,
  hasAnyDigitalWallet,
};

export { SharedPageBuilder };
